import { useState, useEffect, useRef } from 'react';
import { Sun, Phone, Mail, MapPin, Menu, X, Battery, Zap, ChevronDown, LogIn, User, Settings, LogOut, LayoutDashboard, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { LoginModal } from './LoginModal';
import { BrandLogo } from './BrandLogo';
import { useAuthStore } from '../store/authStore';

export function HomeHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsMenuOpen, setSolutionsMenuOpen] = useState(false);
  const [mobileTopBarOpen, setMobileTopBarOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { user, signOut, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    await signOut();
    setProfileMenuOpen(false);
    navigate('/');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  return (
    <header className="bg-white">
      {/* Top Bar */}
      <div className="bg-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm">
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center">
                <Phone className="h-4 w-4 mr-1" />
                <span>920005469</span>
              </div>
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-1" />
                <span>info@gasable.com</span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                <span>Abu Bakr Al Siddiq Road – Al Masif, Riyadh - Saudi Arabia</span>
              </div>
            </div>
            
            {/* Mobile contact toggle */}
            <div className="md:hidden flex justify-center w-full">
              <button 
                onClick={() => setMobileTopBarOpen(!mobileTopBarOpen)}
                className="text-white flex items-center"
              >
                <span>Contact Info</span>
                <ChevronDown className={`h-4 w-4 ml-1 transform transition-transform ${mobileTopBarOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {/* Mobile contact info */}
            {mobileTopBarOpen && (
              <div className="md:hidden space-y-2 mt-2 w-full">
                <div className="flex items-center justify-center">
                  <Phone className="h-4 w-4 mr-1" />
                  <span>(123) 456-7890</span>
                </div>
                <div className="flex items-center justify-center">
                  <Mail className="h-4 w-4 mr-1" />
                  <span>info@entek.ai</span>
                </div>
                <div className="flex items-center justify-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  <span>123 Energy Street, Tech City</span>
                </div>
              </div>
            )}
            
            <div className="mt-2 md:mt-0">
              <a href="#" className="hover:text-blue-200 transition-colors">Schedule a Consultation</a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <div className="flex items-center">
                <BrandLogo className="h-9 w-auto" />
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <div className="relative group">
                <button 
                  className="text-gray-700 hover:text-blue-600 font-medium flex items-center transition-colors"
                  onClick={() => setSolutionsMenuOpen(!solutionsMenuOpen)}
                >
                  Solutions
                  <ChevronDown className="h-4 w-4 ml-1" />
                </button>
                {solutionsMenuOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 flex items-center transition-colors">
                        <Sun className="h-4 w-4 mr-2 text-yellow-500" />
                        Solar Solutions
                      </a>
                      <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 flex items-center transition-colors">
                        <Battery className="h-4 w-4 mr-2 text-green-500" />
                        Battery Storage
                      </a>
                      <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 flex items-center transition-colors">
                        <Zap className="h-4 w-4 mr-2 text-blue-500" />
                        Hybrid Systems
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <Link to="/about-us" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                About Us
              </Link>
              <Link to="/why-solar" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Why Choose Us
              </Link>
              <Link to="/how-it-works" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                How It Works
              </Link>
              <Link to="/subscription" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                Pricing
              </Link>
            </nav>

            <div className="flex items-center space-x-4">
              {isLoading ? (
                <div className="hidden md:flex items-center bg-gray-100 text-gray-600 py-2 px-4 rounded-lg">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </div>
              ) : user ? (
                <div className="relative" ref={profileMenuRef}>
                  <button 
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    className="hidden md:flex items-center bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                  >
                    <User className="h-4 w-4 mr-2" />
                    {user.email}
                  </button>
                  
                  {/* Profile Dropdown */}
                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <button
                        onClick={() => {
                          navigate('/dashboard');
                          setProfileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <LayoutDashboard className="h-4 w-4 mr-3" />
                        Dashboard
                      </button>
                      <button
                        onClick={() => {
                          navigate('/project/new');
                          setProfileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Sun className="h-4 w-4 mr-3" />
                        New Project
                      </button>
                      <button
                        onClick={() => {
                          navigate('/subscription');
                          setProfileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        Settings
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="hidden md:flex items-center bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Login
                </button>
              )}
              
              {/* Mobile menu button */}
              <button 
                className="md:hidden text-gray-700 hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <div>
              <button 
                className="w-full text-left block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setSolutionsMenuOpen(!solutionsMenuOpen)}
              >
                Solutions
              </button>
              {solutionsMenuOpen && (
                <div className="pl-4">
                  <a href="#" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md flex items-center transition-colors">
                    <Sun className="h-4 w-4 mr-2 text-yellow-500" />
                    Solar Solutions
                  </a>
                  <a href="#" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md flex items-center transition-colors">
                    <Battery className="h-4 w-4 mr-2 text-green-500" />
                    Battery Storage
                  </a>
                  <a href="#" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md flex items-center transition-colors">
                    <Zap className="h-4 w-4 mr-2 text-blue-500" />
                    Hybrid Systems
                  </a>
                </div>
              )}
            </div>
            <Link 
              to="/about-us" 
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link 
              to="/why-solar" 
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Why Choose Us
            </Link>
            <Link 
              to="/how-it-works" 
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              How It Works
            </Link>
            <Link 
              to="/subscription" 
              className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pricing
            </Link>
            {isLoading ? (
              <div className="mt-4 flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-600 mr-2" />
                <span className="text-gray-600">Loading...</span>
              </div>
            ) : user ? (
              <div className="mt-4 space-y-2">
                <div className="px-3 py-2 text-sm text-gray-500 border-b border-gray-200">
                  {user.email}
                </div>
                <button
                  onClick={() => {
                    navigate('/dashboard');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors flex items-center"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" />
                  Dashboard
                </button>
                <button
                  onClick={() => {
                    navigate('/project/new');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors flex items-center"
                >
                  <Sun className="h-4 w-4 mr-2" />
                  New Project
                </button>
                <button
                  onClick={() => {
                    navigate('/subscription');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-colors flex items-center"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left block px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            ) : (
              <button 
                onClick={() => {
                  setShowLoginModal(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full mt-4 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login
              </button>
            )}
          </div>
        </div>
      )}

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)}
          onLogin={() => {}}
          error={null}
          onStartNewProject={() => window.location.href = '/new-project'}
        />
      )}
    </header>
  );
}
